import axios from "axios";
import dayjs from "dayjs";
import { Env } from "../styles";
import { parseVacationDateValue } from "./vacationModalDates";

/**
 * نافذة الدوام ليوم معيّن من daily_shift_ranges أو الإعدادات الافتراضية.
 */
export function resolveShiftWindowForDate(
  dateStr,
  dailyShiftRanges,
  durationFallback,
) {
  const shift = dailyShiftRanges?.[dateStr];
  if (shift?.start && shift?.end) {
    const start = dayjs(shift.start);
    const end = dayjs(shift.end);
    if (start.isValid() && end.isValid()) {
      return {
        start,
        end,
        reqSeconds: Math.max(0, end.diff(start, "second")),
      };
    }
  }

  const durationStart = durationFallback?.start || "07:00:00";
  const durationEnd = durationFallback?.end || "14:00:00";
  let start = dayjs(`${dateStr} ${durationStart}`);
  let end = dayjs(`${dateStr} ${durationEnd}`);
  if (!end.isAfter(start)) {
    end = end.add(1, "day");
  }

  return {
    start,
    end,
    reqSeconds: Math.max(0, end.diff(start, "second")),
  };
}

/**
 * تقاطع إجازة مع نافذة دوام يوم واحد (للطباعة والخصميات).
 */
export function computeVacationDayShiftSlice(
  dateStr,
  vacation,
  dailyShiftRanges,
  durationFallback,
) {
  const vFrom = parseVacationDateValue(vacation?.date_from);
  const vTo = parseVacationDateValue(vacation?.date_to);
  if (!vFrom || !vTo || !dateStr) {
    return { from: "", to: "", secs: 0 };
  }

  const win = resolveShiftWindowForDate(
    dateStr,
    dailyShiftRanges,
    durationFallback,
  );
  const sliceStart = vFrom.isAfter(win.start) ? vFrom : win.start;
  const sliceEnd = vTo.isBefore(win.end) ? vTo : win.end;
  if (!sliceEnd.isAfter(sliceStart)) {
    return { from: "", to: "", secs: 0 };
  }

  const overlapSec = sliceEnd.diff(sliceStart, "second");
  const contribSec = Math.min(overlapSec, win.reqSeconds);

  return {
    from: sliceStart.format("HH:mm"),
    to: sliceEnd.format("HH:mm"),
    secs: contribSec,
  };
}

export function shiftWindowToFormRange(windowPayload) {
  if (!windowPayload?.start || !windowPayload?.end) {
    return null;
  }
  const start = dayjs(windowPayload.start);
  const end = dayjs(windowPayload.end);
  if (!start.isValid() || !end.isValid()) {
    return null;
  }
  return {
    fromValue: start.format("YYYY-MM-DD HH:mm"),
    toValue: end.format("YYYY-MM-DD HH:mm"),
  };
}

/** نطاق الدوام من daily_shift_ranges المحمّل مسبقاً (dawam-info) */
export function getLocalShiftRangeForDate(
  dateStr,
  dailyShiftRanges,
  durationFallback,
) {
  const win = resolveShiftWindowForDate(
    dateStr,
    dailyShiftRanges,
    durationFallback,
  );
  if (!win?.start?.isValid?.() || !win?.end?.isValid?.()) {
    return null;
  }
  return {
    fromValue: win.start.format("YYYY-MM-DD HH:mm"),
    toValue: win.end.format("YYYY-MM-DD HH:mm"),
  };
}

/**
 * نافذة دوام اليوم من السيرفر (getDurationStart/End) — تطابق فترة الموظف.
 */
export async function fetchEmployeeShiftWindow(userId, dateStr) {
  if (!userId || !dateStr) {
    return null;
  }
  try {
    const res = await axios.get(
      `${Env.HOST_SERVER_NAME}employee-shift-window/${userId}`,
      { params: { date: dateStr } },
    );
    return shiftWindowToFormRange(res.data);
  } catch {
    return null;
  }
}

let debounceTimer = null;

/**
 * يحسب مدة الإجازة حسب نافذة الدوام لكل يوم (مطابق للباك اند).
 */
export function fetchVacationDurationLabel(userId, start, end, onResult) {
  if (!userId || !start || !end) {
    onResult("");
    return () => {};
  }

  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(async () => {
    try {
      const res = await axios.get(
        `${Env.HOST_SERVER_NAME}vacation-shift-duration/${userId}`,
        { params: { start, end } },
      );
      onResult(res.data?.label || "");
    } catch {
      onResult("");
    }
  }, 280);

  return () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  };
}
